import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setUser } from "../../features/user/userSlice";
import LogoTitle from "../../components/logoTitle";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import LoadingOverlay from "../../components/loadingOverlay";
import { API_BASE } from "../../config/apiConfig";
import { useToast } from "../../hooks/useToast";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();
  const { showToast, ToastContainer } = useToast();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!email) {
      showToast("Email is required.", "error");
      return;
    }
    if (!password) {
      showToast("Password is required.", "error");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json().catch(() => ({}));

      // Handle unverified users
      if (res.status === 403 && data.requires_verification && data.email) {
        let countdown = 5;
        showToast(
          `Email not verified!\nPlease verify your account before logging in.\nRedirecting to verification page in ${countdown}s...`,
          "error"
        );

        const interval = setInterval(() => {
          countdown--;
          showToast(
            `Email not verified!\nPlease verify your account before logging in.\nRedirecting to verification page in ${countdown}s...`,
            "error",
            "top",
            true,
            5500
          );
          if (countdown <= 0) clearInterval(interval);
        }, 1000);

        setTimeout(() => {
          clearInterval(interval);
          navigate("/verify", { state: { email: data.email } });
        }, 5000);
        return;
      }

      if (!res.ok) throw new Error(data?.message || "Login failed.");

      const { access_token, user } = data;

      sessionStorage.setItem("access_token", access_token);
      sessionStorage.setItem("userDetail", JSON.stringify(user));

      dispatch(setUser(user));
      // -----------------------------
      // Handle redirect after login
      // -----------------------------
      const qrRedirect = sessionStorage.getItem("qr_redirect");
      if (qrRedirect) navigate(qrRedirect, { replace: true });
      else navigate("/dashboard", { replace: true });
    } catch (error) {
      const msg = error.message || "Login failed. Please try again.";
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signin-container">
      <div className="w-full max-w-sm rounded-2xl p-6 shadow-[0_10px_40px_rgba(0,0,0,0.2)] bg-white/60 backdrop-blur-lg">
        <div className="flex justify-center mb-6">
          <LogoTitle />
        </div>
        <h2 className="title">Login</h2>

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border rounded border-gray-300 placeholder:italic placeholder:text-gray-400"
              autoComplete="email"
            />
          </div>

          <div className="relative">
            <input
              type={showPw ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border rounded border-gray-300 placeholder:italic placeholder:text-gray-400"
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPw((prev) => !prev)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              tabIndex={-1}
            >
              {showPw ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 rounded disabled:opacity-60"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        {/* Links below form */}
        <div className="mt-3 text-xs text-gray-600 text-left flex flex-col gap-1">
          <span>
            New user?{" "}
            <Link to="/register" className="text-blue-600 hover:underline">
              Click here to register
            </Link>
          </span>
          <span>
            Forgot password?{" "}
            <Link to="/forgot-password" className="text-blue-600 hover:underline">
              Reset it here
            </Link>
          </span>
        </div>
      </div>
      <LoadingOverlay show={loading} text="Loading..." />
      <ToastContainer />
    </div>
  );
};

export default LoginPage;
